<template>
  <header class="admin-header" :style="{ left: headerLeft }">
    <div class="header-inner">
      <div class="header-left">
        <!-- Mobile Menu Button -->
        <button
          @click="adminStore.toggleMobileSidebar()"
          class="mobile-menu-btn"
          :aria-label="$t('admin.layout.toggleMenu')"
        >
          <i class="fas fa-bars"></i>
        </button>

        <!-- Breadcrumb / Page Title -->
        <div class="header-title">
          <span class="title-text">{{ currentPageTitle }}</span>
        </div>
      </div>

      <div class="header-right">
        <!-- Language Switcher (English, Italian, French) -->
        <LanguageSwitcher class="admin-lang-switcher" :allowed-locales="['en', 'it', 'fr']" />

        <!-- Search Button -->
        <button class="header-btn" :title="$t('admin.layout.search')">
          <i class="fas fa-search"></i>
        </button>

        <!-- Notifications -->
        <NotificationDropdown />

        <!-- User Menu -->
        <div class="user-menu-wrapper">
          <button @click="toggleUserMenu" class="user-menu-trigger">
            <div class="user-avatar">
              <ClientOnly>
                <span>{{ adminStore.userDisplayName.charAt(0).toUpperCase() }}</span>
              </ClientOnly>
            </div>
            <div class="user-info">
              <ClientOnly>
                <p class="user-name">{{ adminStore.userDisplayName }}</p>
                <p class="user-role">{{ adminStore.user?.role_label || 'Admin' }}</p>
              </ClientOnly>
            </div>
            <i class="fas fa-chevron-down dropdown-icon"></i>
          </button>

          <!-- Dropdown Menu -->
          <Transition name="dropdown">
            <div v-if="showUserMenu" class="user-dropdown">
              <div class="dropdown-header">
                <ClientOnly>
                  <p class="dropdown-name">{{ adminStore.userDisplayName }}</p>
                  <p class="dropdown-email">{{ adminStore.user?.email || 'admin@technomac.com' }}</p>
                </ClientOnly>
              </div>
              <div class="dropdown-divider"></div>
              <NuxtLink
                to="/admin/profile"
                class="dropdown-item"
                @click="showUserMenu = false"
              >
                <i class="fas fa-user"></i>
                {{ $t('admin.userMenu.profileSettings') }}
              </NuxtLink>
              <NuxtLink
                to="/admin/notifications"
                class="dropdown-item"
                @click="showUserMenu = false"
              >
                <i class="fas fa-bell"></i>
                {{ $t('admin.userMenu.notifications') }}
              </NuxtLink>
              <div class="dropdown-divider"></div>
              <button @click="handleLogout" class="dropdown-item logout">
                <i class="fas fa-sign-out-alt"></i>
                {{ $t('admin.userMenu.signOut') }}
              </button>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
const adminStore = useAdminStore()
const router = useRouter()
const route = useRoute()

const showUserMenu = ref(false)

// Dynamic header left position based on sidebar state
const headerLeft = computed(() => {
  if (typeof window !== 'undefined' && window.innerWidth < 1024) {
    return '0'
  }
  return adminStore.sidebarWidth
})

// Get current page title from route
const currentPageTitle = computed(() => {
  const path = route.path
  const segments = path.split('/').filter(Boolean)
  if (segments.length <= 1) return 'Dashboard'
  const lastSegment = segments[segments.length - 1]
  return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1).replace(/-/g, ' ')
})

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

const handleLogout = async () => {
  showUserMenu.value = false
  try {
    await adminStore.logout()
    await router.push('/admin/login')
  } catch (error) {
    console.error('Logout error:', error)
  }
}

// Close dropdown on outside click
onMounted(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest('.user-menu-wrapper')) {
      showUserMenu.value = false
    }
  }
  document.addEventListener('click', handleClickOutside)
  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })
})
</script>

<style scoped>
.admin-header {
  position: fixed;
  top: 0;
  right: 0;
  height: 64px;
  background: var(--admin-card-bg, #ffffff);
  border-bottom: 1px solid var(--admin-border-color, #e5e7eb);
  z-index: 40;
  transition: left 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding: 0 1.5rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.mobile-menu-btn {
  display: none;
  padding: 0.625rem;
  border: none;
  background: transparent;
  color: var(--admin-text-secondary, #6b7280);
  border-radius: var(--admin-radius-md, 0.5rem);
  cursor: pointer;
  transition: all 0.2s;
}

.mobile-menu-btn:hover {
  background: var(--admin-bg-secondary, #f3f4f6);
  color: var(--admin-text-primary, #111827);
}

.mobile-menu-btn i {
  font-size: 1.25rem;
}

.header-title {
  display: none;
}

.title-text {
  font-size: 1rem;
  font-weight: 600;
  color: var(--admin-text-primary, #111827);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-btn {
  padding: 0.625rem;
  border: none;
  background: transparent;
  color: var(--admin-text-secondary, #6b7280);
  border-radius: var(--admin-radius-md, 0.5rem);
  cursor: pointer;
  transition: all 0.2s;
}

.header-btn:hover {
  background: var(--admin-bg-secondary, #f3f4f6);
  color: var(--admin-primary, #6366f1);
}

.header-btn i {
  font-size: 1rem;
}

/* User Menu */
.user-menu-wrapper {
  position: relative;
}

.user-menu-trigger {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.375rem 0.5rem;
  border: none;
  background: transparent;
  border-radius: var(--admin-radius-lg, 0.75rem);
  cursor: pointer;
  transition: all 0.2s;
}

.user-menu-trigger:hover {
  background: var(--admin-bg-secondary, #f3f4f6);
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: var(--admin-radius-lg, 0.75rem);
  background: var(--admin-gradient-primary, linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.user-info {
  display: none;
  text-align: left;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--admin-text-primary, #111827);
  margin: 0;
  line-height: 1.2;
}

.user-role {
  font-size: 0.75rem;
  color: var(--admin-text-muted, #9ca3af);
  margin: 0;
}

.dropdown-icon {
  font-size: 0.625rem;
  color: var(--admin-text-muted, #9ca3af);
  transition: transform 0.2s;
}

.user-menu-trigger:hover .dropdown-icon {
  color: var(--admin-text-secondary, #6b7280);
}

/* Dropdown */
.user-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 0.5rem);
  width: 240px;
  background: var(--admin-card-bg, #ffffff);
  border: 1px solid var(--admin-border-color, #e5e7eb);
  border-radius: var(--admin-radius-lg, 0.75rem);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  z-index: 50;
}

.dropdown-header {
  padding: 1rem;
  background: linear-gradient(to bottom, #f8fafc, #f1f5f9);
}

.dropdown-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--admin-text-primary, #111827);
  margin: 0;
}

.dropdown-email {
  font-size: 0.75rem;
  color: var(--admin-text-muted, #9ca3af);
  margin: 0.25rem 0 0;
}

.dropdown-divider {
  height: 1px;
  background: var(--admin-border-color, #e5e7eb);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: var(--admin-text-secondary, #6b7280);
  text-decoration: none;
  transition: all 0.15s;
  border: none;
  background: none;
  width: 100%;
  cursor: pointer;
}

.dropdown-item:hover {
  background: var(--admin-bg-secondary, #f3f4f6);
  color: var(--admin-text-primary, #111827);
}

.dropdown-item i {
  width: 16px;
  text-align: center;
  font-size: 0.875rem;
}

.dropdown-item.logout {
  color: var(--admin-danger, #ef4444);
}

.dropdown-item.logout:hover {
  background: var(--admin-danger-50, #fef2f2);
  color: var(--admin-danger-600, #dc2626);
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Admin Language Switcher */
:deep(.admin-lang-switcher) {
  margin-right: 0.25rem;
}

:deep(.admin-lang-switcher .language-button) {
  padding: 0.5rem 0.625rem;
  border: 1px solid var(--admin-border-color, #e5e7eb);
  border-radius: var(--admin-radius-md, 0.5rem);
  background: var(--admin-card-bg, #ffffff);
  transition: all 0.2s;
  box-shadow: none;
}

:deep(.admin-lang-switcher .language-button:hover) {
  background: var(--admin-bg-secondary, #f3f4f6);
  border-color: var(--admin-primary, #6366f1);
  box-shadow: none;
}

:deep(.admin-lang-switcher .language-dropdown) {
  border: 1px solid var(--admin-border-color, #e5e7eb);
  border-radius: var(--admin-radius-lg, 0.75rem);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 100;
}

:deep(.admin-lang-switcher .language-option) {
  transition: all 0.15s;
}

:deep(.admin-lang-switcher .language-option:hover) {
  background: var(--admin-bg-secondary, #f3f4f6);
}

:deep(.admin-lang-switcher .language-option.active) {
  background: var(--admin-primary-50, #eef2ff);
  color: var(--admin-primary, #6366f1);
}

:deep(.admin-lang-switcher .loading-spinner) {
  margin-left: 0.25rem;
}

:deep(.admin-lang-switcher .loading-spinner::after) {
  border-color: var(--admin-primary, #6366f1);
  border-top-color: transparent;
}

/* Responsive */
@media (min-width: 640px) {
  .user-info {
    display: block;
  }

  .header-title {
    display: block;
  }
}

@media (max-width: 1023px) {
  .admin-header {
    left: 0 !important;
  }

  .mobile-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
